import { useState } from 'react';

import type { StoredCaptureEvent } from '../../lib/types';
import { cn } from '../../lib/utils';

// ─── Props ──────────────────────────────────────────────────────────────────

interface TimelineRailProps {
  events: StoredCaptureEvent[];
  startAt: number;
  totalDurationMs: number;
  selectedIdx: number | null;
  onSelect: (idx: number) => void;
  capabilityColors: Record<string, string>;
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function asRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : null;
}

function asString(value: unknown): string | null {
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return null;
}

function cleanUrl(input: string, max = 40): string {
  try {
    const url = new URL(input);
    const path = `${url.pathname}${url.search}`;
    return path.length > max ? path.slice(0, max) + '…' : path;
  } catch {
    return input.length > max ? input.slice(0, max) + '…' : input;
  }
}

function parseFetchPayload(payload: Record<string, unknown>): {
  url: string;
  method: string;
  status?: number | undefined;
  ok?: boolean | undefined;
} {
  const isArrayLike = '0' in payload;
  const url = isArrayLike
    ? (asString(payload['0']) ?? '')
    : (asString(payload['input']) ?? asString(payload['url']) ?? '');
  const init = isArrayLike ? (asRecord(payload['1']) ?? null) : (asRecord(payload['init']) ?? null);
  const method = (asString(init?.['method']) ?? asString(payload['method']) ?? 'GET').toUpperCase();
  const status = payload['status'] !== undefined ? Number(payload['status']) : undefined;
  const ok = payload['ok'] !== undefined ? Boolean(payload['ok']) : undefined;
  return { url, method, status, ok };
}

function getConsoleMessage(payload: Record<string, unknown>): string {
  const msg =
    asString(payload['message']) ?? asString(payload['text']) ?? asString(payload['value']);
  if (msg != null) return msg.length > 56 ? msg.slice(0, 56) + '…' : msg;
  const args = payload['args'];
  if (Array.isArray(args) && args.length > 0) {
    const s = String(args[0]);
    return s.length > 56 ? s.slice(0, 56) + '…' : s;
  }
  return 'console output';
}

// ─── Event classification ───────────────────────────────────────────────────

type EventKind = 'lifecycle' | 'fetch' | 'log' | 'error' | 'other';

function classifyEvent(event: StoredCaptureEvent): EventKind {
  if (event.phase === 'error') return 'error';
  if (event.capability === 'vm') return 'lifecycle';
  if (event.capability === 'fetch') return 'fetch';
  if (event.capability === 'console') return 'log';
  return 'other';
}

type Filter = 'all' | 'network' | 'logs' | 'errors';

const FILTERS: { id: Filter; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'network', label: 'Network' },
  { id: 'logs', label: 'Logs' },
  { id: 'errors', label: 'Errors' },
];

function matchesFilter(event: StoredCaptureEvent, filter: Filter): boolean {
  if (filter === 'all') return true;
  if (filter === 'network') return event.capability === 'fetch';
  if (filter === 'logs') return event.capability === 'console';
  if (filter === 'errors') return event.phase === 'error';
  return true;
}

// ─── Event row ──────────────────────────────────────────────────────────────

function EventRow({
  event,
  idx: _idx,
  origIdx,
  isSelected,
  isCritical,
  startAt,
  total,
  onSelect,
  capabilityColors,
}: {
  event: StoredCaptureEvent;
  idx: number;
  origIdx: number; // index in the original events array for selection
  isSelected: boolean;
  isCritical: boolean;
  startAt: number;
  total: number;
  onSelect: (idx: number) => void;
  capabilityColors: Record<string, string>;
}): import('react/jsx-runtime').JSX.Element {
  const kind = classifyEvent(event);
  const offset = Math.round(event.at - startAt);
  const color = capabilityColors[event.capability] ?? 'var(--flux-console)';
  const isResponse = event.phase === 'response';
  const isError = event.phase === 'error' || event.errorPayload !== undefined;
  const isSpan = event.phase === 'span';
  const payload = asRecord(event.payload);
  const reqPayload = asRecord(event.requestPayload);
  const resPayload = asRecord(event.responsePayload);

  // ── Primary label ──────────────────────────────────────────────────────────
  let primaryLabel: React.ReactNode;
  let sublabel: string | null = null;

  if (kind === 'lifecycle') {
    const isBoot = event.action === 'boot';
    primaryLabel = (
      <span
        className={cn(
          'font-semibold',
          isBoot
            ? 'text-foreground/90'
            : isError
              ? 'text-[var(--flux-error)]'
              : 'text-foreground/90',
        )}
      >
        {isBoot ? 'function called' : 'function returned'}
      </span>
    );
  } else if (kind === 'fetch' && (payload || reqPayload)) {
    const { url, method } = parseFetchPayload(reqPayload ?? payload ?? {});
    const { status, ok } = parseFetchPayload(resPayload ?? payload ?? {});
    const path = url ? cleanUrl(url) : null;
    if (isResponse || (isSpan && resPayload)) {
      // Response row: show method + status + URL
      const statusColor =
        ok === false || isError ? 'text-[var(--flux-error)]' : 'text-[var(--flux-success)]';
      primaryLabel = (
        <span className="flex items-center gap-1.5 min-w-0">
          <span className="text-[9px] font-bold font-mono px-1 py-px rounded bg-blue-500/15 text-blue-400 shrink-0">
            {method}
          </span>
          <span className={cn('font-semibold font-mono tabular-nums shrink-0', statusColor)}>
            {status ?? '—'}
          </span>
          {path != null && (
            <span className="text-muted-foreground/50 truncate text-[10px]">{path}</span>
          )}
        </span>
      );
    } else {
      // Request row: method badge + path
      primaryLabel = (
        <span className="flex items-center gap-1.5 min-w-0">
          <span className="text-[9px] font-bold font-mono px-1 py-px rounded bg-blue-500/15 text-blue-400 shrink-0">
            {method}
          </span>
          {path != null ? (
            <span className="truncate text-foreground/80 font-medium">{path}</span>
          ) : (
            <span className="text-muted-foreground/50">fetch</span>
          )}
        </span>
      );
    }
  } else if (kind === 'log' && payload) {
    const msg = getConsoleMessage(payload);
    const level = event.action; // log, warn, error, info
    const levelColor =
      level === 'error'
        ? 'text-[var(--flux-error)]'
        : level === 'warn'
          ? 'text-[var(--flux-warning)]'
          : 'text-muted-foreground/70';
    primaryLabel = <span className={cn('truncate', levelColor)}>{msg}</span>;
  } else if (kind === 'error' && payload) {
    const msg = asString(payload['message']) ?? asString(payload['error']) ?? 'error';
    primaryLabel = (
      <span className="text-[var(--flux-error)] font-medium truncate">
        {msg.length > 48 ? msg.slice(0, 48) + '…' : msg}
      </span>
    );
  } else {
    const fnName = (event as unknown as Record<string, unknown>)['fnName'];
    const resolvedFnName = typeof fnName === 'string' ? fnName : undefined;
    if (event.capability === 'rpc' && event.action === 'call') {
      primaryLabel = (
        <span className="font-semibold text-blue-400 truncate">{resolvedFnName ?? 'rpc'}()</span>
      );
    } else {
      primaryLabel = (
        <span className="text-muted-foreground/60">
          {resolvedFnName ?? `${event.capability}.${event.action}`}
        </span>
      );
      if (payload && !isSpan) sublabel = event.phase;
    }
  }

  // ── Dot style per kind ─────────────────────────────────────────────────────
  const dotStyle: React.CSSProperties = {
    backgroundColor: isError ? 'var(--flux-error)' : color,
    opacity: isResponse ? 0.5 : 1,
  };
  const dotSize = kind === 'lifecycle' ? 'h-2.5 w-2.5' : isResponse ? 'h-1.5 w-1.5' : 'h-2 w-2';
  if (isSpan) dotStyle.opacity = 1;

  return (
    <button
      onClick={(): void => onSelect(origIdx)}
      className={cn(
        'w-full text-left px-3 py-2 transition-all duration-75 border-l-2 group',
        isSelected
          ? 'bg-white/[0.06] border-l-blue-400'
          : isCritical && !isSelected
            ? 'border-l-[var(--flux-warning)]/60 hover:bg-white/[0.04]'
            : 'border-l-transparent hover:bg-white/[0.03]',
      )}
    >
      <div className="flex items-center gap-2 min-w-0">
        {/* Critical path marker */}
        {isCritical && (
          <span className="shrink-0 text-[8px] font-bold font-mono text-[var(--flux-warning)] bg-[var(--flux-warning)]/10 px-1 py-px rounded">
            SLOW
          </span>
        )}
        {/* Status dot */}
        <span className={cn('inline-block shrink-0 rounded-full', dotSize)} style={dotStyle} />
        {/* Label */}
        <span className="text-[11px] font-mono min-w-0 flex-1 truncate">{primaryLabel}</span>
        {/* Duration on same row for fetch responses */}
        {event.durationMs !== undefined && event.durationMs > 0 && (
          <span
            className={cn(
              'text-[9px] font-mono tabular-nums shrink-0 ml-1',
              event.durationMs > 500
                ? 'text-[var(--flux-error)]'
                : event.durationMs > 100
                  ? 'text-[var(--flux-warning)]'
                  : 'text-muted-foreground/35',
            )}
          >
            {event.durationMs}ms
          </span>
        )}
      </div>

      {/* Sub-label row: offset + secondary text */}
      <div className="flex items-center gap-3 mt-0.5 pl-[18px]">
        <span className="text-[9px] font-mono text-muted-foreground/30 tabular-nums">
          +{offset}ms
        </span>
        {sublabel != null && (
          <span className="text-[9px] text-muted-foreground/30">{sublabel}</span>
        )}
      </div>

      {/* Duration bar */}
      {event.durationMs !== undefined && event.durationMs > 0 && (
        <div className="h-[2px] mx-[18px] mt-1 rounded-full bg-white/[0.04] relative overflow-hidden">
          <div
            className={cn(
              'absolute left-0 top-0 h-full rounded-full transition-all',
              isCritical
                ? 'bg-[var(--flux-warning)]/60'
                : event.durationMs > 500
                  ? 'bg-[var(--flux-error)]/50'
                  : event.durationMs > 100
                    ? 'bg-[var(--flux-warning)]/40'
                    : 'bg-white/20',
            )}
            style={{ width: `${Math.min(100, (event.durationMs / total) * 100)}%` }}
          />
        </div>
      )}
    </button>
  );
}

// ─── Main component ──────────────────────────────────────────────────────────

export function TimelineRail({
  events,
  startAt,
  totalDurationMs,
  selectedIdx,
  onSelect,
  capabilityColors,
}: TimelineRailProps): import('react/jsx-runtime').JSX.Element {
  const [filter, setFilter] = useState<Filter>('all');
  const total = totalDurationMs > 0 ? totalDurationMs : 1;

  // Find critical path: event with highest durationMs
  const criticalIdx = events.reduce<number | null>((best, e, i): number | null => {
    if ((e.durationMs ?? 0) === 0) return best;
    if (best === null) return i;
    return (e.durationMs ?? 0) > (events[best]!.durationMs ?? 0) ? i : best;
  }, null);

  // Filter — keep original indices for selection callbacks
  const filtered: { event: StoredCaptureEvent; origIdx: number }[] = events
    .map((event, origIdx) => ({ event, origIdx }))
    .filter(({ event }): boolean => matchesFilter(event, filter));

  const counts = {
    all: events.length,
    network: events.filter((e): boolean => matchesFilter(e, 'network')).length,
    logs: events.filter((e): boolean => matchesFilter(e, 'logs')).length,
    errors: events.filter((e): boolean => matchesFilter(e, 'errors')).length,
  };

  return (
    <div className="w-72 shrink-0 flex flex-col overflow-hidden bg-[#0a0c11]/40 h-full">
      {/* Header + filters */}
      <div className="shrink-0 border-b border-white/5">
        <div className="flex items-center justify-between px-3 py-2">
          <h3 className="text-[10px] font-semibold text-muted-foreground/40 uppercase tracking-widest">
            Timeline
          </h3>
          <span className="text-[10px] text-muted-foreground/30 font-mono tabular-nums">
            {events.length} events
          </span>
        </div>
        {/* Quick filters */}
        <div className="flex items-center gap-0.5 px-2 pb-2">
          {FILTERS.map((f): import('react/jsx-runtime').JSX.Element => {
            const count = counts[f.id];
            const active = filter === f.id;
            return (
              <button
                key={f.id}
                onClick={(): void => setFilter(f.id)}
                className={cn(
                  'flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-mono transition-all',
                  active
                    ? 'bg-white/[0.08] text-foreground border border-white/10'
                    : 'text-muted-foreground/40 hover:text-muted-foreground/70 hover:bg-white/[0.04]',
                )}
              >
                {f.label}
                {f.id !== 'all' && count > 0 && (
                  <span
                    className={cn(
                      'text-[8px] font-bold',
                      f.id === 'errors' ? 'text-[var(--flux-error)]' : 'text-muted-foreground/40',
                    )}
                  >
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Event list */}
      <div className="flex-1 overflow-y-auto">
        {filtered.length === 0 ? (
          <div className="px-3 py-8 text-center text-[11px] text-muted-foreground/30">
            No {filter} events
          </div>
        ) : (
          filtered.map(({ event, origIdx }, idx): import('react/jsx-runtime').JSX.Element => (
            <EventRow
              key={`${event.seq}-${event.id}`}
              event={event}
              idx={idx}
              origIdx={origIdx}
              isSelected={origIdx === selectedIdx}
              isCritical={origIdx === criticalIdx}
              startAt={startAt}
              total={total}
              onSelect={onSelect}
              capabilityColors={capabilityColors}
            />
          ))
        )}
      </div>
    </div>
  );
}
